import {
	Button,
	Dialog,
	DialogClose,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	Input,
	PaginatedDataTable,
	TextArea,
	useDebounce,
} from "@dune/ui";
import { Plus } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import {
	CreateInsurerDto,
	InsurerWithSociety,
	UpdateInsurerDto,
} from "@dune/types";
import { getInsurerColumns } from "./insurerColumns";
import {
	useCreateInsurer,
	useDeleteInsurer,
	useGetAllInsurers,
	useUpdateInsurer,
} from "../../api/queries/insurers-admin.queries";
import { useMutateGetSocietyInfoFromSiren } from "../../api/queries/siren.queries";
import PageLayoutWithTabs from "../../component/layout/PageLayoutWithTabs";
import { usePagination } from "../../hooks/usePagination";

const InsurersManagementPage: React.FC = () => {
	const { page, limit, setPage, handleLimitChange } = usePagination();
	const { data: insurersResponse, isLoading } = useGetAllInsurers({
		page,
		limit,
	});
	const insurers = insurersResponse?.data || [];
	const paginationMeta = insurersResponse?.meta || {
		page,
		limit,
		total: 0,
		totalPages: 1,
		hasNextPage: false,
		hasPreviousPage: false,
	};
	const deleteInsurer = useDeleteInsurer();
	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
	const [selectedInsurer, setSelectedInsurer] =
		useState<InsurerWithSociety | null>(null);

	const columns = getInsurerColumns({
		onEdit: (insurer: InsurerWithSociety) => {
			setSelectedInsurer(insurer);
			setIsEditDialogOpen(true);
		},
		onDelete: (id: number) => {
			if (window.confirm("Êtes-vous sûr de vouloir supprimer cet assureur ?")) {
				deleteInsurer.mutate(id, {
					onSuccess: () => {
						toast.success("Succès", {
							description: "L'assureur a été supprimé avec succès",
						});
					},
					onError: () => {
						toast.error("Erreur", {
							description: "Erreur lors de la suppression de l'assureur",
						});
					},
				});
			}
		},
	});

	return (
		<PageLayoutWithTabs title="Gestion des assureurs" tabContent={{}}>
			<div className="flex flex-col gap-6">
				<div className="flex justify-between items-center">
					<p className="text-gray-600">Liste des assureurs de la plateforme</p>
					<Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
						<DialogTrigger asChild>
							<Button>
								<Plus className="mr-2 h-4 w-4" />
								Ajouter un assureur
							</Button>
						</DialogTrigger>
						<DialogContent className="max-w-2xl">
							<DialogHeader>
								<DialogTitle>Ajouter un nouvel assureur</DialogTitle>
							</DialogHeader>
							<CreateInsurerForm
								onSuccess={() => {
									setIsDialogOpen(false);
									toast.success("Succès", {
										description: "L'assureur a été créé avec succès",
									});
								}}
								onCancel={() => setIsDialogOpen(false)}
							/>
						</DialogContent>
					</Dialog>

					<Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
						<DialogContent className="max-w-2xl">
							<DialogHeader>
								<DialogTitle>Modifier l'assureur</DialogTitle>
							</DialogHeader>
							{selectedInsurer && (
								<EditInsurerForm
									insurer={selectedInsurer}
									onSuccess={() => {
										setIsEditDialogOpen(false);
										setSelectedInsurer(null);
										toast.success("Succès", {
											description: "L'assureur a été modifié avec succès",
										});
									}}
									onCancel={() => {
										setIsEditDialogOpen(false);
										setSelectedInsurer(null);
									}}
								/>
							)}
						</DialogContent>
					</Dialog>
				</div>

				<PaginatedDataTable
					columns={columns}
					data={insurers}
					meta={paginationMeta}
					onPageChange={setPage}
					onLimitChange={handleLimitChange}
					isLoading={isLoading}
					noResultsText="Aucun assureur trouvé"
				/>
			</div>
		</PageLayoutWithTabs>
	);
};
interface CreateInsurerFormProps {
	onSuccess: () => void;
	onCancel: () => void;
}

const CreateInsurerForm: React.FC<CreateInsurerFormProps> = ({
	onSuccess,
	onCancel,
}) => {
	const createInsurer = useCreateInsurer();
	const { mutate: getSocietyInfo } = useMutateGetSocietyInfoFromSiren();

	const form = useForm<CreateInsurerDto>({
		defaultValues: {
			siren: "",
			capital: undefined,
			registrationNumber: "",
			legalNotices: "",
		},
	});

	const sirenValue = form.watch("siren");
	const debouncedSiren = useDebounce(sirenValue, 500);
	const [sirenSearchStatus, setSirenSearchStatus] = useState<
		"idle" | "searching" | "success" | "error"
	>("idle");
	const [societyName, setSocietyName] = useState<string>("");

	useEffect(() => {
		if (debouncedSiren && debouncedSiren.length === 9) {
			setSirenSearchStatus("searching");
			setSocietyName("");

			getSocietyInfo(debouncedSiren, {
				onSuccess: (data: { denomination?: string }) => {
					if (data?.denomination) {
						setSocietyName(data.denomination);
						setSirenSearchStatus("success");
						form.clearErrors("siren");
					} else {
						setSirenSearchStatus("error");
						form.setError("siren", {
							type: "manual",
							message: "Aucune société trouvée pour ce SIREN",
						});
					}
				},
				onError: () => {
					setSirenSearchStatus("error");
					form.setError("siren", {
						type: "manual",
						message: "Erreur lors de la recherche du SIREN",
					});
				},
			});
		} else if (debouncedSiren && debouncedSiren.length > 0) {
			setSirenSearchStatus("idle");
			setSocietyName("");
		}
	}, [debouncedSiren, getSocietyInfo, form]);

	const onSubmit = (data: CreateInsurerDto) => {
		createInsurer.mutate(data, {
			onSuccess: () => {
				onSuccess();
				form.reset();
			},
			onError: (error) => {
				const errorMessage =
					error.message ?? "Erreur lors de la création de l'assureur";
				toast.error("Erreur", {
					description: errorMessage,
				});
			},
		});
	};

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
				<FormField
					control={form.control}
					name="siren"
					rules={{
						required: "Le SIREN est requis",
						pattern: {
							value: /^\d{9}$/,
							message: "Le SIREN doit contenir exactement 9 chiffres",
						},
					}}
					render={({ field }) => (
						<FormItem>
							<FormLabel>SIREN</FormLabel>
							<FormControl>
								<Input placeholder="123456789" {...field} maxLength={9} />
							</FormControl>
							<FormDescription>
								{sirenSearchStatus === "searching" && "Recherche en cours..."}
								{sirenSearchStatus === "success" &&
									societyName &&
									`Société trouvée : ${societyName}`}
								{sirenSearchStatus === "error" && "Société non trouvée"}
							</FormDescription>
							<FormMessage />
						</FormItem>
					)}
				/>

				<FormField
					control={form.control}
					name="capital"
					render={({ field }) => (
						<FormItem>
							<FormLabel>Capital (optionnel)</FormLabel>
							<FormControl>
								<Input
									type="number"
									placeholder="10000"
									{...field}
									value={field.value ?? ""}
									onChange={(e) => {
										const value = e.target.value;
										field.onChange(value ? Number(value) : undefined);
									}}
								/>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				<FormField
					control={form.control}
					name="registrationNumber"
					render={({ field }) => (
						<FormItem>
							<FormLabel>Numéro d'inscription (optionnel)</FormLabel>
							<FormControl>
								<Input placeholder="ABC123456" {...field} />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				<FormField
					control={form.control}
					name="legalNotices"
					render={({ field }) => (
						<FormItem>
							<FormLabel>Mentions légales (optionnel)</FormLabel>
							<FormControl>
								<TextArea
									placeholder="Mentions légales de l'assureur..."
									className="min-h-[100px]"
									{...field}
									value={field.value ?? ""}
								/>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				<DialogFooter>
					<DialogClose asChild>
						<Button type="button" variant="outline" onClick={onCancel}>
							Annuler
						</Button>
					</DialogClose>
					<Button
						type="submit"
						disabled={
							createInsurer.isPending || sirenSearchStatus !== "success"
						}
					>
						{createInsurer.isPending ? "Création..." : "Créer"}
					</Button>
				</DialogFooter>
			</form>
		</Form>
	);
};

interface EditInsurerFormProps {
	insurer: InsurerWithSociety;
	onSuccess: () => void;
	onCancel: () => void;
}

const EditInsurerForm: React.FC<EditInsurerFormProps> = ({
	insurer,
	onSuccess,
	onCancel,
}) => {
	const updateInsurer = useUpdateInsurer();

	const form = useForm<UpdateInsurerDto>({
		defaultValues: {
			capital: insurer.capital ?? undefined,
			registrationNumber: insurer.registrationNumber ?? "",
			legalNotices: insurer.legalNotices ?? "",
		},
	});

	const onSubmit = (data: UpdateInsurerDto) => {
		updateInsurer.mutate(
			{ id: insurer.id, payload: data },
			{
				onSuccess: () => {
					onSuccess();
					form.reset();
				},
				onError: (error: unknown) => {
					const errorMessage =
						(error as { response?: { data?: { message?: string } } })?.response
							?.data?.message || "Erreur lors de la modification de l'assureur";
					toast.error("Erreur", {
						description: errorMessage,
					});
				},
			},
		);
	};

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
				<div className="mb-4 p-4 bg-gray-50 rounded-md">
					<p className="text-sm text-gray-600">
						<span className="font-semibold">Société :</span>{" "}
						{insurer.society.denomination}
					</p>
					<p className="text-sm text-gray-600">
						<span className="font-semibold">SIREN :</span>{" "}
						{insurer.society.siren}
					</p>
				</div>

				<FormField
					control={form.control}
					name="capital"
					render={({ field }) => (
						<FormItem>
							<FormLabel>Capital (optionnel)</FormLabel>
							<FormControl>
								<Input
									type="number"
									placeholder="10000"
									{...field}
									value={field.value ?? ""}
									onChange={(e) => {
										const value = e.target.value;
										field.onChange(value ? Number(value) : undefined);
									}}
								/>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				<FormField
					control={form.control}
					name="registrationNumber"
					render={({ field }) => (
						<FormItem>
							<FormLabel>Numéro d'inscription (optionnel)</FormLabel>
							<FormControl>
								<Input placeholder="ABC123456" {...field} />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				<FormField
					control={form.control}
					name="legalNotices"
					render={({ field }) => (
						<FormItem>
							<FormLabel>Mentions légales (optionnel)</FormLabel>
							<FormControl>
								<TextArea
									placeholder="Mentions légales de l'assureur..."
									className="min-h-[100px]"
									{...field}
									value={field.value ?? ""}
								/>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				<DialogFooter>
					<DialogClose asChild>
						<Button type="button" variant="outline" onClick={onCancel}>
							Annuler
						</Button>
					</DialogClose>
					<Button type="submit" disabled={updateInsurer.isPending}>
						{updateInsurer.isPending ? "Modification..." : "Modifier"}
					</Button>
				</DialogFooter>
			</form>
		</Form>
	);
};

export default InsurersManagementPage;
